import Comment from "../../entities/comment";

/**
 * Comment actions
 *
 * Call order:
 *
 * COMMENT_SAVE_QUEUE:	adds the item to the save queue
 * --> COMMENT_SAVE:	modifies the state
 * --> COMMENT_SAVE_SUCCESS: if all went well
 * --> COMMENT_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail COMMENT_SAVE_QUEUE is during the call to COMMENT_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at COMMENT_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: COMMENT_SAVE_SUCCESS, COMMENT_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or COMMENT_PENDING_CHECK (it left the queue).
 */

export const COMMENT_FETCH_ONE_QUEUE: any = 'COMMENT_FETCH_ONE_QUEUE';
export const COMMENT_FETCH_SUCCESS: any = 'COMMENT_FETCH_SUCCESS';
export const COMMENT_FETCH_FAILURE: any = 'COMMENT_FETCH_FAILURE';
export const COMMENT_FETCH_ONE_SUCCESS: any = 'COMMENT_FETCH_ONE_SUCCESS';
export const COMMENT_FETCH_ONE_FAILURE: any = 'COMMENT_FETCH_ONE_FAILURE';

export const COMMENT_SAVE: any = 'COMMENT_SAVE';
export const COMMENT_SAVE_QUEUE: any = 'COMMENT_SAVE_QUEUE';
export const COMMENT_SAVE_SUCCESS: any = 'COMMENT_SAVE_SUCCESS';
export const COMMENT_SAVE_FAILURE: any = 'COMMENT_SAVE_FAILURE';

export const COMMENT_SAVE_MANY: any = 'COMMENT_SAVE_MANY';
export const COMMENT_SAVE_MANY_QUEUE: any = 'COMMENT_SAVE_MANY_QUEUE';
export const COMMENT_SAVE_MANY_SUCCESS: any = 'COMMENT_SAVE_MANY_SUCCESS';
export const COMMENT_SAVE_MANY_FAILURE: any = 'COMMENT_SAVE_MANY_FAILURE';

export const COMMENT_REMOVE: any = 'COMMENT_REMOVE';
export const COMMENT_REMOVE_QUEUE: any = 'COMMENT_REMOVE_QUEUE';
export const COMMENT_REMOVE_SUCCESS: any = 'COMMENT_REMOVE_SUCCESS';
export const COMMENT_REMOVE_FAILURE: any = 'COMMENT_REMOVE_FAILURE';

export const COMMENT_RESTORE: any = 'COMMENT_RESTORE';
export const COMMENT_RESTORE_QUEUE: any = 'COMMENT_RESTORE_QUEUE';
export const COMMENT_RESTORE_SUCCESS: any = 'COMMENT_RESTORE_SUCCESS';
export const COMMENT_RESTORE_FAILURE: any = 'COMMENT_RESTORE_FAILURE';

export const COMMENT_RESTORE_MANY: any = 'COMMENT_RESTORE_MANY';
export const COMMENT_REMOVE_MANY: any = 'COMMENT_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const COMMENT_SAVE_REVERT: any = 'COMMENT_SAVE_REVERT';
export const COMMENT_PENDING_CHECK: any = 'COMMENT_PENDING_CHECK';

/* Fetch actions */
export const fetchCommentSuccess = (comments: Comment[], partial: boolean, lastFetch: number) => ({type: COMMENT_FETCH_SUCCESS, comments, partial, lastFetch});
export const fetchCommentFailure = (err: any) => {
		console.error('CommentAction: fetchCommentFailure', err);
		return {type: COMMENT_FETCH_FAILURE, err};
};
export const fetchOneCommentQueue = (commentId: string, sentAddedOn?: number) => ({type: COMMENT_FETCH_ONE_QUEUE, commentId, sentAddedOn});
export const fetchOneCommentSuccess = (comment: Comment | null, sentAddedOn?: number, commentId: string = comment?.id) => ({type: COMMENT_FETCH_ONE_SUCCESS, commentId, comment, sentAddedOn});
export const fetchOneCommentFailure = (id: string, err: any) => {
		console.error('CommentAction: fetchOneCommentFailure', err);
		return {type: COMMENT_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveComment = (comment: Comment) => {
    // console.debug('⮂ COMMENT_SAVE action', comment?.id);
    return {type: COMMENT_SAVE, comment};
}
export const saveCommentQueue = (comment: Comment, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ COMMENT_SAVE_QUEUE action', comment?.id);
    return {type: COMMENT_SAVE_QUEUE, comment, onQueued};
}
export const saveCommentSuccess = (comment: Comment, sentAddedOn?: number) => {
    // console.debug('⮂ COMMENT_SAVE_SUCCESS action', comment?.id);
    return {type: COMMENT_SAVE_SUCCESS, comment, sentAddedOn};
}
export const saveCommentFailure = (comment: Comment, status: number, message: string) => {
    console.warn(`⮂ COMMENT_SAVE_FAILURE error saving comment: HTTP ${status}: ${message}`, comment?.id);
    return {type: COMMENT_SAVE_FAILURE, comment, status, message};
};

/* SaveMany actions */
export const saveManyComments = (comments: Comment[]) => {
    // console.debug('⮂ COMMENT_SAVE_MANY action', comments?.map(comment => comment?.id).join(','));
    return {type: COMMENT_SAVE_MANY, comments};
}
export const saveManyCommentsQueue = (comments: Comment[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ COMMENT_SAVE_MANY_QUEUE action', comments?.map(comment => comment.id).join(','));
    return {type: COMMENT_SAVE_MANY_QUEUE, comments, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `comments` (OWA-1018) */
export const saveManyCommentsSuccess = (comments: Comment[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ COMMENT_SAVE_MANY_SUCCESS action', comments?.map(comment => comment.id).join(','));
    return {type: COMMENT_SAVE_MANY_SUCCESS, comments, sentAddedOns};
}
export const saveManyCommentsFailure = (comments: Comment[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_COMMENTS_FAILURE Error saving comments: HTTP ${status}: ${message}`, comments?.map(comment => comment.id).join(','));
		return {type: COMMENT_SAVE_MANY_FAILURE, comments, status, message};
};

/* Remove actions */
export const removeComment = (comment: Comment) => ({ type: COMMENT_REMOVE, comment });
export const removeCommentQueue = (comment: Comment) => ({ type: COMMENT_REMOVE_QUEUE, comment });
export const removeCommentSuccess = (comment: Comment) => ({ type: COMMENT_REMOVE_SUCCESS, comment });
export const removeCommentFailure = (comment: Comment, status: number, message: string) => {
	console.error(`Redux: removeCommentFailure Error removing comment: HTTP ${status}: ${message}`);
	return ({ type: COMMENT_REMOVE_FAILURE, comment, status, message });
};

/* Restore actions */
export const restoreComment = (comment: Comment) => ({ type: COMMENT_RESTORE, comment });
export const restoreCommentQueue = (comment: Comment) => ({ type: COMMENT_RESTORE_QUEUE, comment });
export const restoreCommentSuccess = (comment: Comment, sentAddedOn?: number) => ({ type: COMMENT_RESTORE_SUCCESS, comment, sentAddedOn });
export const restoreCommentFailure = (comment: Comment, status: number, message: string) => {
	console.error(`Redux: restoreCommentFailure Error restoring comment: HTTP ${status}: ${message}`);
	return ({ type: COMMENT_RESTORE_FAILURE, comment, status, message });
};

/* RestoreMany actions */
export const restoreManyComments = (comments: Comment[]) => ({ type: COMMENT_RESTORE_MANY, comments });

/* RemoveMany actions */
export const removeManyComments = (comments: Comment[]) => ({ type: COMMENT_REMOVE_MANY, comments });

/* Pending save actions (OWA-1018) */
/**
 * The save `comment` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertCommentSave = (comment: Comment, options: {sentAddedOn?: number, current?: Comment | null, keep?: boolean} = {}) =>
    ({type: COMMENT_SAVE_REVERT, comment, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkCommentPendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: COMMENT_PENDING_CHECK, live, refused, readAt});